// Voice + Text surface — Vella 2.0 Base (Command Center).
// Contract reversed 2026-04-23 — accepts BOTH voice AND text input.
// Voice path → LiveKit/WebRTC (continuous, barge-in handled by the agent).
// Mints a token via /api/voice/token, joins a `vella-call-{userId}-{ts}`
// room, publishes the local mic, and plays whatever audio the Vella voice
// agent publishes back. Text path is handled by the global InlineChatBar
// in App.jsx — this component does NOT render its own chatbar.
// Single-chatbar UX as of 2026-04-27.
// Session persists to localStorage (last 50 messages).
// See: docs/talk-to-vella-ux-contract.md for the full invariants list.
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { FONT, BLUE, TEXT, TEXT2, TEXT3, SUCCESS, WARNING, DANGER, BORDER, setVellaThinking, useIsMobile, VellaAvatar } from '../shared';
import { createLiveKitClient } from '../lib/livekitClient';
import { fetchVoiceToken } from '../lib/voiceToken';
import { VellaOrb } from './VellaOrb';
import { LivingOrb } from './LivingOrb';
import { BreathGrid } from './BreathGrid';
import { ShiningText } from './ui/ShiningText';
import { aiCustom, aiSwarm } from '../api';
import VellaChatMessage from './preview/VellaChatMessage';
import AnalyticsWidget from './widgets/AnalyticsWidget';
import InboxWidget from './widgets/InboxWidget';
import CampaignWidget from './widgets/CampaignWidget';
import SwarmResultWidget from './widgets/SwarmResultWidget';
import ActionButtons from './widgets/ActionButtons';
import RelayScreenWidget from './widgets/RelayScreenWidget';
import SeoAuditWidget from './widgets/SeoAuditWidget';
import CreativeWidget from './widgets/CreativeWidget';
import WebsitePreviewCard from './widgets/WebsitePreviewCard';
import WebsitePreviewCardUi from './ui/WebsitePreviewCard';
import ResultPreviewPanel from './ResultPreviewPanel';
import { detectVellaContent } from '../lib/detectVellaContent';
import Markdown from './ui/Markdown';
import {
  getActiveId,
  newConversation,
  updateConversationMessages,
  getConversation,
} from '../lib/conversationStore';
import { AutopilotContext } from '../App';

const SESSION_KEY = 'vella_chat_session';
const MAX_PERSISTED = 50;

// The orb's mid-tone "baby blue" (see VellaOrb gradient stops) — used for the
// idle-home headline and other accents that should match the orb.
const ORB_BABY_BLUE = '#60a5fa';

const CREATIVE_THEME_CARDS = [
  { id: "lifestyle",    label: "Lifestyle Scene",    desc: "Product in real-world context" },
  { id: "showcase",     label: "Product Showcase",   desc: "Clean, focused spotlight" },
  { id: "before-after", label: "Before & After",     desc: "Transform the narrative" },
  { id: "testimonial",  label: "User Testimonial",   desc: "Real voices, real results" },
  { id: "bts",          label: "Behind the Scenes",  desc: "Authentic brand storytelling" },
  { id: "educational",  label: "Tips & Education",   desc: "Share your expertise" },
  { id: "seasonal",     label: "Seasonal Moment",    desc: "Timely, relevant content" },
  { id: "comparison",   label: "Comparison",         desc: "Side-by-side brand win" },
  { id: "influencer",   label: "Influencer Style",   desc: "Aspirational creator look" },
  { id: "announcement", label: "Announcement",       desc: "Launch, reveal, milestone" },
];

const CREATIVE_RE = /\b(create|post|ad|reel|story|stories|content|design|campaign|creative|generate|produce)\b/i;

// ─── Result derivation ──────────────────────────────────────────────────────
// Classify the last assistant turn into a previewable result so the VellaOrb
// can morph to its "result" state and the right-docked split panel can open.
// Returns null when the turn produced nothing worth surfacing (plain reply).
//
//   { previewType: 'website'|'image'|'ad'|'post',
//     orb:     { kind, title, url?, image? },              // VellaOrb result card
//     payload: { kind:'website'|'creative', previewType, ...fields } }  // panel
function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s; }

function extractHtml(content, vellaContent) {
  if (vellaContent?.html) return vellaContent.html;
  const c = content || '';
  const fence = c.match(/```(?:html|HTML)\s*\n([\s\S]+?)\n```/);
  if (fence) return fence[1];
  const doc = c.match(/<!DOCTYPE[\s\S]+?<\/html>/i) || c.match(/<html[\s\S]+?<\/html>/i);
  return doc ? doc[0] : '';
}

function deriveResult(msg) {
  if (!msg) return null;

  // 1) Website — explicit widget, vellaContent envelope, or inline HTML doc.
  const websiteWidget = (msg.widgets || []).find(
    w => w?.type === 'website' || w?.type === 'website_preview'
  );
  if (websiteWidget) {
    const d = websiteWidget.data || {};
    const title = d.businessName || d.title || 'Your site';
    const url = d.url || d.preview_url || undefined;
    const image = d.image || d.screenshot || undefined;
    return {
      previewType: 'website',
      orb: { kind: 'website', title, url, image },
      payload: { kind: 'website', previewType: 'website', title, url, image, html: d.html || undefined },
    };
  }
  if (msg.vellaContent?.type === 'website' || /<!DOCTYPE|<html[\s>]/i.test(msg.content || '')) {
    const html = extractHtml(msg.content, msg.vellaContent);
    const title = msg.vellaContent?.title || msg.vellaContent?.businessName || 'Your site';
    const url = msg.vellaContent?.url || undefined;
    return {
      previewType: 'website',
      orb: { kind: 'website', title, url, image: msg.vellaContent?.image || undefined },
      payload: { kind: 'website', previewType: 'website', title, url, html: html || undefined, image: msg.vellaContent?.image || undefined },
    };
  }

  // 2) Structured creative preview — image / ad / social post.
  const sp = msg.structured_preview;
  if (sp) {
    const image = sp.asset?.url || sp.asset?.image || sp.image || sp.url || undefined;
    const variations = (
      Array.isArray(sp.variations) ? sp.variations
      : Array.isArray(sp.variants) ? sp.variants
      : Array.isArray(sp.assets) ? sp.assets.map(a => a?.url || a)
      : []
    ).map(v => (typeof v === 'string' ? v : v?.url || v?.image)).filter(Boolean);
    const isAd = sp.kind === 'ad' || !!sp.adType || !!sp.headline || !!sp.cta;
    const isPost = !isAd && (!!sp.platform || !!sp.caption || (Array.isArray(sp.hashtags) && sp.hashtags.length > 0));
    const previewType = isAd ? 'ad' : isPost ? 'post' : 'image';
    const title = sp.headline || sp.title
      || (previewType === 'ad' ? 'New Ad'
        : previewType === 'post' ? `${cap(sp.platform) || 'Social'} Post`
        : 'New Creative');
    return {
      previewType,
      orb: { kind: previewType, title, image },
      payload: {
        kind: 'creative',
        previewType,
        title,
        image,
        variations,
        caption: sp.caption || undefined,
        headline: sp.headline || undefined,
        cta: sp.cta || sp.ctaText || undefined,
        platform: sp.platform || undefined,
        hashtags: Array.isArray(sp.hashtags) ? sp.hashtags : undefined,
        brand: sp.brand || undefined,
      },
    };
  }

  // 3) Text-only fallback via the shared detector.
  const detected = detectVellaContent(msg.content, msg.vellaContent);
  if (detected) {
    if (detected.kind === 'website') {
      const title = detected.title || 'Your site';
      return {
        previewType: 'website',
        orb: { kind: 'website', title, url: detected.url },
        payload: { kind: 'website', previewType: 'website', title, url: detected.url, html: detected.html || undefined },
      };
    }
    const previewType = detected.kind === 'ad' ? 'ad' : 'post';
    const image = detected.asset?.url || undefined;
    const title = detected.headline
      || (previewType === 'ad' ? 'New Ad' : `${cap(detected.platform) || 'Social'} Post`);
    return {
      previewType,
      orb: { kind: previewType, title, image },
      payload: {
        kind: 'creative',
        previewType,
        title,
        image,
        caption: detected.caption || undefined,
        headline: detected.headline || undefined,
        cta: detected.cta || undefined,
        platform: detected.platform || undefined,
        hashtags: detected.hashtags || undefined,
        brand: detected.brand || undefined,
      },
    };
  }

  return null;
}

// Turn a thrown api() error into specific, user-readable copy. The api()
// helper attaches .kind / .status / .code / .requestId (see src/api.js), so we
// can name the actual failure ("HTTP 500", "rate limited", "session expired")
// and surface the request id that matches the server logs — instead of a
// blanket "something went wrong". Mirrors brainErrorCopy in App.jsx; inlined
// here to avoid importing from the root App component.
// Read a File as a base64 data URL ("data:image/png;base64,…"). Used to send
// image/PDF attachments to the backend, which converts them to Claude vision /
// document content blocks (services/claude-capabilities.js). The data URL also
// works directly as an <img> preview src — no upload round-trip needed.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("file read failed"));
    reader.readAsDataURL(file);
  });
}

function describeBrainError(err) {
  if (!err) return "Vella couldn't reach the brain just now. Please try again.";
  const ridSuffix = err.requestId ? ` · ref ${String(err.requestId).slice(0, 8)}` : "";
  switch (err.kind) {
    case "kill_switch":
      return err.code === "global_kill_switch_active"
        ? `Vella is paused for a quick maintenance window. Try again in a few minutes.${ridSuffix}`
        : `Your Vella access is paused. Email support@amplifx.com and we'll get you back online.${ridSuffix}`;
    case "subscription":
      return `Your subscription needs attention before Vella can keep helping. Open Settings → Billing.${ridSuffix}`;
    case "rate_limit":
      return `That was a lot at once. Give Vella ~30 seconds, then retry.${ridSuffix}`;
    case "auth":
      return `Your session expired — reloading…${ridSuffix}`;
    case "network":
      return `I can't reach the server. Check your connection and retry.${ridSuffix}`;
    case "server":
      return `Vella's brain hit an error (HTTP ${err.status || "5xx"}: ${err.message || "server error"}). Retry in a moment.${ridSuffix}`;
    case "client":
      return `That request didn't go through (HTTP ${err.status || "4xx"}: ${err.message || "client error"}).${ridSuffix}`;
    default:
      return `Vella couldn't reach the brain just now${err.message ? ` (${err.message})` : ""}. Please retry.${ridSuffix}`;
  }
}

// Ocean-blue filled / glass-outline action button shown under an orb result.
function OrbActionBtn({ primary, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="liquid-press"
      style={{
        cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600,
        padding: '9px 18px', borderRadius: 999,
        color: primary ? '#fff' : TEXT,
        background: primary ? BLUE : 'rgba(255,255,255,0.6)',
        border: primary ? `1px solid ${BLUE}` : '1px solid rgba(255,255,255,0.7)',
        backdropFilter: primary ? 'none' : 'blur(14px)',
        WebkitBackdropFilter: primary ? 'none' : 'blur(14px)',
        boxShadow: primary
          ? '0 8px 20px -8px rgba(39,92,115,0.5)'
          : 'inset 0 1px 0 rgba(255,255,255,0.85)',
        transition: 'filter 160ms ease',
      }}
    >
      {children}
    </button>
  );
}

// Iteration suggestion chip (rounded glass pill).
function OrbChip({ primary, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        cursor: 'pointer', fontFamily: FONT, fontSize: 12.5, fontWeight: 600,
        padding: '7px 14px', borderRadius: 999,
        color: primary ? '#0369A1' : TEXT,
        border: '1px solid',
        borderColor: primary ? 'rgba(14,165,233,0.55)' : 'rgba(15,23,42,0.16)',
        background: primary
          ? 'linear-gradient(135deg, rgba(14,165,233,0.16) 0%, rgba(14,165,233,0.10) 100%)'
          : 'rgba(255,255,255,0.65)',
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)',
        transition: 'background 160ms ease, transform 160ms ease',
      }}
    >
      {children}
    </button>
  );
}

// Idle-home quick-start chips moved to App.jsx (docked above InlineChatBar)
// so they live in the same container as the chat bar and move with it.

// ─── Context action inference ──────────────────────────────────────────────
// Client-side fallback when backend doesn't return a `widgets.actions` block.
// Only three contexts surface chips — website, creative, analytics — so we
// never show irrelevant actions on an unrelated reply.
function inferContextActions(replyText) {
  if (!replyText) return null;
  const lower = replyText.toLowerCase();
  // Website
  if (/website|landing page|web page|webpage|site is live|page was published|published your site|built (?:your|the) site/.test(lower)) {
    return [
      { label: 'Preview site', action: 'preview_site', variant: 'primary' },
      { label: 'Edit site', action: 'edit_site', icon: 'edit', variant: 'ghost' },
      { label: 'Regenerate', action: 'regenerate', icon: 'regenerate', variant: 'ghost' },
    ];
  }
  // Creative (image / video / post / ad / caption)
  if (/creative|image|photo|graphic|reel|story|stories|post\b|caption|design|visual|\bad\b|carousel/.test(lower)) {
    return [
      { label: 'Another angle', action: 'another_angle', variant: 'primary' },
      { label: 'Different look', action: 'different_look', icon: 'regenerate', variant: 'ghost' },
      { label: 'Use this', action: 'use_this', variant: 'success' },
    ];
  }
  // Analytics
  if (/analytics|impressions|reach|engagement|followers|stats|performance|metrics/.test(lower)) {
    return [
      { label: 'Show detail', action: 'analytics_detail', icon: 'analytics_detail', variant: 'primary' },
      { label: 'Export report', action: 'export', icon: 'export', variant: 'ghost' },
    ];
  }
  return null;
}

// ─── Website-HTML extraction ───────────────────────────────────────────────
// When the backend doesn't return a `website_preview` widget explicitly but
// the assistant reply contains generated HTML (raw <!DOCTYPE…> document or a
// fenced ```html``` block), we synthesize one so the chat thread renders a
// WebsitePreviewCard instead of dumping the source into a text bubble.

// Strip EVERY code fence and raw HTML/markup fragment out of an assistant
// reply so generated source can never leak into a chat bubble. Returns the
// human-readable prose that's left over (may be empty).
function stripWebsiteMarkup(text) {
  if (!text || typeof text !== 'string') return '';
  let out = text;
  out = out.replace(/```[\s\S]*?```/g, ' ');          // fenced code blocks
  out = out.replace(/```[\s\S]*$/g, ' ');              // unterminated trailing fence
  out = out.replace(/<!DOCTYPE[\s\S]+?<\/html>/gi, ' '); // full documents
  out = out.replace(/<html[\s\S]+?<\/html>/gi, ' ');
  out = out.replace(/<style[\s\S]*?<\/style>/gi, ' ');   // stray style/script blocks
  out = out.replace(/<script[\s\S]*?<\/script>/gi, ' ');
  out = out.replace(/<\/?[a-z][\s\S]*?>/gi, ' ');        // any remaining tags
  out = out
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
  return out;
}

// Pull a usable HTML document out of the reply. Returns
// { widget, cleanText, title } or null when there's no embedded site.
function extractWebsiteHtml(text, businessNameHint) {
  if (!text || typeof text !== 'string') return null;
  let html = null;
  const fence = text.match(/```(?:html|HTML)?\s*\n?([\s\S]+?)```/);
  if (fence && /<!DOCTYPE|<html[\s>]/i.test(fence[1])) {
    html = fence[1].trim();
  } else {
    const m = text.match(/<!DOCTYPE[\s\S]+?<\/html>/i) || text.match(/<html[\s\S]+?<\/html>/i);
    if (m) html = m[0];
  }
  if (!html || html.length < 200) return null;
  let title = businessNameHint || '';
  if (!title) {
    const t = html.match(/<title[^>]*>([^<]+)<\/title>/i);
    if (t) title = t[1].trim();
  }
  return {
    widget: { type: 'website_preview', data: { html, businessName: title || 'Your site' } },
    cleanText: stripWebsiteMarkup(text),
    title: title || 'Your site',
  };
}

const WEBSITE_BUILT_FALLBACK = "Here's the site I built. Tap the preview to open the editor.";
const HAS_MARKUP_RE = /```|<!DOCTYPE|<html[\s>]/i;

// ─── Utility: load / save session ────────────────────────────────────────
// The conversation store owns the per-conversation message lists. This
// helper boots the active conversation (creating one if there isn't an
// active id yet, and migrating the legacy flat session if present).
function loadInitialMessages() {
  let id = getActiveId();
  if (!id) {
    // Migrate any legacy flat session into a fresh conversation.
    let legacy = [];
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      legacy = raw ? JSON.parse(raw) : [];
    } catch { legacy = []; }
    id = newConversation();
    if (Array.isArray(legacy) && legacy.length) {
      updateConversationMessages(id, legacy);
    }
  }
  const convo = getConversation(id);
  // Fresh page loads land on the hero (orb + headline): if the active
  // conversation already has messages, boot a new empty thread instead of
  // resuming it. The prior conversation stays available in the sidebar.
  // An already-empty active conversation is reused, so reloading without
  // chatting doesn't pile up blank "New conversation" entries.
  if (convo?.messages?.length) {
    return { id: newConversation(), messages: [] };
  }
  return { id, messages: convo?.messages || [] };
}
function saveSessionLegacy(messages) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(messages.slice(-MAX_PERSISTED)));
  } catch {}
}

// ─── Icons ─────────────────────────────────────────────────────────────────

function MicIcon({ size = 16, color = "#0EA5E9", filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"} stroke={color} strokeWidth="1.7">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  );
}

function SendIcon({ size = 16, color = "#FFFFFF" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function PaperclipIcon({ size = 16, color = TEXT3 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function UserAvatar({ size = 30 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      boxShadow: '0 2px 8px rgba(14,165,233,0.30), inset 0 1px 0 rgba(255,255,255,0.35)',
      color: '#FFFFFF',
    }} aria-hidden="true">
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    </div>
  );
}

// D6 — voice-turn marker. A spoken message renders as an ordinary chat bubble
// tagged with this mic glyph instead of its own transcript card.
function MicGlyph({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-label="Voice message" style={{ opacity: 0.7 }}>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0M12 17v4" />
    </svg>
  );
}

function SwarmIcon({ size = 14, color = "#9333EA" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

// ─── Uploaded image thumbnail ───────────────────────────────────────────────
function UploadPreview({ file, url, onRemove }) {
  const src = url || (file ? URL.createObjectURL(file) : null);
  if (!src) return null;
  return (
    <div style={{ position: 'relative', display: 'inline-flex', marginBottom: 8 }}>
      <img src={src} alt="Attachment" style={{ height: 56, width: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(14,165,233,0.35)' }} />
      <button
        onClick={onRemove}
        style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: DANGER, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
        aria-label="Remove attachment"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  );
}

// ─── Widgets renderer ──────────────────────────────────────────────────────
function MessageWidgets({ widgets, swarmAgents, onAction }) {
  if (!widgets?.length && !swarmAgents?.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 10 }}>
      {swarmAgents?.length > 0 && <SwarmResultWidget agents={swarmAgents} />}
      {widgets?.map((w, i) => {
        switch (w.type) {
          case 'analytics':     return <AnalyticsWidget     key={i} data={w.data}       onAction={onAction} />;
          case 'inbox':         return <InboxWidget         key={i} data={w.data}       onAction={onAction} />;
          case 'campaign':      return <CampaignWidget      key={i} data={w.data}       onAction={onAction} />;
          case 'relay_screen':  return <RelayScreenWidget   key={i} data={w.data}       onAction={onAction} />;
          case 'seo_audit':     return <SeoAuditWidget      key={i} data={w.data}       onAction={onAction} />;
          case 'seo':           return <SeoAuditWidget      key={i} data={w.data}       onAction={onAction} />;
          case 'creative':      return <CreativeWidget      key={i} data={w.data}       onAction={onAction} />;
          case 'website':       return <WebsitePreviewCard  key={i} data={w.data}       onAction={onAction} />;
          case 'website_preview': return <WebsitePreviewCard key={i} data={w.data}      onAction={onAction} />;
          case 'actions':       return <ActionButtons       key={i} buttons={w.buttons} onAction={onAction} />;
          default:              return null;
        }
      })}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
export default function TalkToVella({ setActive, vellaPrompt, clearVellaPrompt }) {
  // ─── Autopilot context (read-only, for the indicator badge) ──────────
  const autopilotCtx = useContext(AutopilotContext);
  const isMobile = useIsMobile();
  const autopilotOn = autopilotCtx?.autopilot ?? false;

  // ─── Voice states ────────────────────────────────────────────────────
  const [isListening,  setIsListening]  = useState(false);
  const [isSpeaking,   setIsSpeaking]   = useState(false);
  const [isThinking,   setIsThinking]   = useState(false);
  const [voiceActive,  setVoiceActive]  = useState(false);
  const [transcript,   setTranscript]   = useState("");
  const [response,     setResponse]     = useState("");
  const [status,       setStatus]       = useState("What do you need help with today?");
  const [error,        setError]        = useState("");
  // Unified morph element (Operation Metamorph). ONE LivingOrb drives every
  // state: idle → listening (input focus) → thinking / card (send) → success
  // (response), then back to idle. Artifact jobs morph the orb INTO a
  // generating card (vella:orbCard); plain chat just spins the docked orb
  // (vella:orbThinking). Driven by window events from the chat bar (App.jsx).
  const [livingOrbState, setLivingOrbState] = useState('idle'); // idle|listening|thinking|success
  const [orbMode, setOrbMode] = useState('orb');                // 'orb' | 'card'
  const [orbTaskType, setOrbTaskType] = useState('website');    // website|image|campaign|ads|video
  const successTimerRef = useRef(null);
  useEffect(() => {
    const clearSuccessTimer = () => {
      if (successTimerRef.current) { clearTimeout(successTimerRef.current); successTimerRef.current = null; }
    };
    // Focus shouldn't downgrade an in-flight thinking/card back to listening.
    const toListening = () => setLivingOrbState((p) => (p === 'thinking' ? p : 'listening'));
    const toIdle = () => setLivingOrbState((p) => (p === 'listening' ? 'idle' : p));
    const toThinking = () => { clearSuccessTimer(); setOrbMode('orb'); setLivingOrbState('thinking'); };
    // Artifact job → morph the shell into the generating card with the right verb set.
    const toCard = (e) => {
      clearSuccessTimer();
      const t = e?.detail?.taskType;
      if (t) setOrbTaskType(t);
      setOrbMode('card');
      setLivingOrbState('thinking');
    };
    const toSuccess = () => {
      clearSuccessTimer();
      // Morph the card back to an orb, fire the burst, then settle to idle.
      setOrbMode('orb');
      setLivingOrbState('success');
      successTimerRef.current = setTimeout(() => setLivingOrbState('idle'), 2200);
    };
    // Stop → morph back, NO burst, straight to idle.
    const toStop = () => { clearSuccessTimer(); setOrbMode('orb'); setLivingOrbState('idle'); };
    window.addEventListener('vella:orbListening', toListening);
    window.addEventListener('vella:orbIdle', toIdle);
    window.addEventListener('vella:orbThinking', toThinking);
    window.addEventListener('vella:orbCard', toCard);
    window.addEventListener('vella:orbSuccess', toSuccess);
    window.addEventListener('vella:orbStop', toStop);
    return () => {
      clearSuccessTimer();
      window.removeEventListener('vella:orbListening', toListening);
      window.removeEventListener('vella:orbIdle', toIdle);
      window.removeEventListener('vella:orbThinking', toThinking);
      window.removeEventListener('vella:orbCard', toCard);
      window.removeEventListener('vella:orbSuccess', toSuccess);
      window.removeEventListener('vella:orbStop', toStop);
    };
  }, []);

  // ─── Text-path state — persisted via conversation store ───────────
  const [inputText,  setInputText]  = useState("");
  const _initialConvo = React.useMemo(loadInitialMessages, []);
  const [conversationId, setConversationId] = useState(_initialConvo.id);
  const [messages,   setMessages]   = useState(_initialConvo.messages);
  const [isSending,  setIsSending]  = useState(false);
  // Set to the user's text inside sendMessage's catch so handleTextSend can
  // restore the composer (don't make the user retype) and so the retry path
  // can resend the exact same message.
  const sendFailedRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Smart auto-scroll: only follow new messages when the user is already near
  // the bottom. If they scroll up to read history, stop yanking them down;
  // resume once they scroll back near the bottom.
  const isNearBottomRef = useRef(true);
  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // ─── Swarm mode ─────────────────────────────────────────────────────
  const [swarmMode, setSwarmMode] = useState(false);

  // ─── File upload ────────────────────────────────────────────────────
  const [uploadedFile,    setUploadedFile]    = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [isUploading,     setIsUploading]     = useState(false);
  const fileInputRef = useRef(null);

  // ─── LiveKit refs ───────────────────────────────────────────────────
  // Voice path joins a `vella-call-{userId}-{ts}` room — the LiveKit Cloud
  // dispatch rule routes the room to the Vella voice agent (Claude +
  // Deepgram + ElevenLabs). The browser is a pure A/V client: publish mic,
  // subscribe to remote audio. Continuous + barge-in are owned by the
  // agent.
  const lkClientRef         = useRef(null);
  const remoteAudioElRef    = useRef(null);
  const remoteAudioTrackRef = useRef(null);
  const voiceActiveRef      = useRef(false);
  const agentConnectedRef   = useRef(false);
  // Track which TranscriptionSegment ids we've already inserted as chat
  // messages so retransmits / final-after-interim don't duplicate.
  const seenSegmentIdsRef   = useRef(new Set());
  // Watchdog: if the agent worker doesn't join the room within N seconds,
  // we surface a clear error instead of leaving the user staring at
  // "Listening..." forever (LiveKit dispatch / Railway agent issues).
  const agentJoinTimerRef   = useRef(null);
  const AGENT_JOIN_TIMEOUT_MS = 12000;

  // Mirror isThinking into the global vella-thinking body class.
  useEffect(() => { setVellaThinking(isThinking); return () => setVellaThinking(false); }, [isThinking]);

  // Persist on every change — conversation store + legacy mirror.
  useEffect(() => {
    if (conversationId) updateConversationMessages(conversationId, messages);
    saveSessionLegacy(messages);
  }, [messages, conversationId]);

  // Cmd+/ → global toggle voice. We use a ref so the listener can call
  // the latest toggleVoiceMode without re-attaching on every render.
  const toggleVoiceModeRef = useRef(null);
  useEffect(() => {
    const onToggleVoice = () => toggleVoiceModeRef.current?.();
    window.addEventListener('vella:toggleVoice', onToggleVoice);
    return () => window.removeEventListener('vella:toggleVoice', onToggleVoice);
  }, []);

  // Conversation handler (new chat). The conversation-history sidebar was
  // removed — only the global 56px nav rail remains.
  const handleSidebarNew = useCallback((id) => {
    setConversationId(id);
    setMessages([]);
    setTranscript("");
    setResponse("");
    setInputText("");
  }, []);

  // ─── LiveKit lifecycle ──────────────────────────────────────────────
  function getOrCreateClient() {
    if (lkClientRef.current) return lkClientRef.current;
    const client = createLiveKitClient();

    client.on('agentAudio', async (track) => {
      agentConnectedRef.current = true;
      if (agentJoinTimerRef.current) {
        clearTimeout(agentJoinTimerRef.current);
        agentJoinTimerRef.current = null;
      }
      remoteAudioTrackRef.current = track;
      const el = remoteAudioElRef.current;
      if (el) {
        try { track.attach(el); }
        catch (err) { console.warn('[TalkToVella] track.attach failed:', err); }
        // Force playback inside the user-gesture chain; some browsers
        // (Safari) ignore autoplay on programmatically-attached
        // MediaStream tracks.
        try { await el.play(); }
        catch (err) {
          console.warn('[TalkToVella] audio.play() failed:', err);
          if (voiceActiveRef.current) {
            setError("Tap the screen to enable audio playback, then try again.");
          }
        }
      }
      setIsSpeaking(true);
      setIsListening(false);
      setIsThinking(false);
      setStatus("Speaking...");
    });

    client.on('agentConnected', () => {
      agentConnectedRef.current = true;
      if (agentJoinTimerRef.current) {
        clearTimeout(agentJoinTimerRef.current);
        agentJoinTimerRef.current = null;
      }
      setStatus("Listening...");
    });

    client.on('agentDisconnected', () => {
      setIsSpeaking(false);
      setIsListening(false);
      setIsThinking(false);
      setStatus("What do you need help with today?");
    });

    // ── Transcript ingestion ───────────────────────────────────────────
    // Two paths land here, both push final segments into the chat thread:
    //   1. RoomEvent.TranscriptionReceived (livekit-client's first-class
    //      transcription channel — what modern agent SDKs publish to).
    //   2. RoomEvent.DataReceived JSON payloads of shape
    //      {type:"transcript"|"transcription", role, text, final}
    //      — what handwritten agents (incl. the current Vella worker)
    //      tend to publish.
    // We dedupe by id (or a synthesized id for path 2) so retransmits
    // don't double-insert.

    function appendFinalsToThread(role, finalsArr) {
      if (!finalsArr.length) return;
      setMessages((prev) => [
        ...prev,
        ...finalsArr.map((s) => ({
          role: role === 'user' ? 'user' : 'assistant',
          content: s.text,
          ts: Date.now(),
          source: 'voice',
        })),
      ]);
    }

    client.on('transcription', ({ role, segments }) => {
      if (!Array.isArray(segments) || segments.length === 0) return;
      const last = segments[segments.length - 1];
      if (last?.text) {
        if (role === 'user') setTranscript(last.text);
        else setResponse(last.text);
      }
      const seen = seenSegmentIdsRef.current;
      const finals = segments.filter((s) => s && s.final && s.text && !seen.has(s.id));
      if (finals.length === 0) return;
      finals.forEach((s) => seen.add(s.id));
      appendFinalsToThread(role, finals);
    });

    // Some agent implementations publish transcripts as JSON data messages
    // instead of (or alongside) the LiveKit Transcription API. Parse them
    // so the chat thread fills in either way. Unknown payloads are
    // silently ignored — this listener is additive, not a replacement.
    client.on('dataMessage', ({ payload, participant }) => {
      if (!payload || typeof payload.byteLength !== 'number') return;
      let text;
      try { text = new TextDecoder('utf-8', { fatal: false }).decode(payload); }
      catch (_) { return; }
      if (!text || text[0] !== '{') return;
      let obj;
      try { obj = JSON.parse(text); }
      catch (_) { return; }
      if (!obj || (obj.type !== 'transcript' && obj.type !== 'transcription')) return;
      const txt = obj.text || obj.content;
      if (typeof txt !== 'string' || !txt.trim()) return;
      // Role precedence: explicit obj.role → infer from participant
      // identity (local participant ⇒ "user") → fallback "assistant".
      let role = obj.role;
      if (role !== 'user' && role !== 'assistant') {
        const localId = lkClientRef.current?.getRoom?.()?.localParticipant?.identity;
        role = participant && localId && participant.identity === localId
          ? 'user'
          : 'assistant';
      }
      if (role === 'user') setTranscript(txt);
      else setResponse(txt);
      const final = obj.final !== false; // default to final if not specified
      if (!final) return;
      const id = obj.id || obj.event_id || `data-${role}-${Date.now()}-${txt.length}`;
      const seen = seenSegmentIdsRef.current;
      if (seen.has(id)) return;
      seen.add(id);
      appendFinalsToThread(role, [{ text: txt }]);
    });

    client.on('connectionStateChanged', (state) => {
      if (state === 'disconnected') {
        setIsListening(false);
        setIsSpeaking(false);
        setIsThinking(false);
        setStatus(voiceActiveRef.current ? "Reconnecting..." : "What do you need help with today?");
      } else if (state === 'connected') {
        if (voiceActiveRef.current) setStatus("Listening...");
      }
    });

    client.on('error', (err) => {
      console.warn('[TalkToVella] livekit error:', err);
      if (voiceActiveRef.current) setError(err?.message || "Voice connection error");
    });

    lkClientRef.current = client;
    return client;
  }

  const startVoice = useCallback(async () => {
    setError("");
    voiceActiveRef.current = true;
    agentConnectedRef.current = false;
    setVoiceActive(true);
    setStatus("Connecting...");
    try {
      const meta = await fetchVoiceToken();
      const client = getOrCreateClient();
      await client.connect({ token: meta.token, url: meta.url });
      await client.publishMicrophone();
      setIsListening(true);
      setStatus("Listening...");
      // Watchdog: bail out if the agent never joins. The LiveKit Cloud
      // dispatch rule routes the room to the Vella agent; if it doesn't
      // pick up, the user would otherwise stare at "Listening..." forever.
      if (agentJoinTimerRef.current) clearTimeout(agentJoinTimerRef.current);
      agentJoinTimerRef.current = setTimeout(() => {
        if (!voiceActiveRef.current || agentConnectedRef.current) return;
        setError("Vella's voice agent didn't pick up. Try again in a moment.");
        try { lkClientRef.current?.disconnect(); } catch (_) {}
        voiceActiveRef.current = false;
        setVoiceActive(false);
        setIsListening(false);
        setIsSpeaking(false);
        setStatus("What do you need help with today?");
      }, AGENT_JOIN_TIMEOUT_MS);
    } catch (err) {
      console.warn('[TalkToVella] startVoice failed:', err);
      setError(err?.message || "Could not start voice session");
      voiceActiveRef.current = false;
      setVoiceActive(false);
      setIsListening(false);
      setStatus("What do you need help with today?");
      try { await lkClientRef.current?.disconnect(); } catch (_) {}
    }
  }, []);

  const stopVoice = useCallback(async () => {
    voiceActiveRef.current = false;
    agentConnectedRef.current = false;
    seenSegmentIdsRef.current.clear();
    if (agentJoinTimerRef.current) {
      clearTimeout(agentJoinTimerRef.current);
      agentJoinTimerRef.current = null;
    }
    setVoiceActive(false);
    setIsListening(false);
    setIsSpeaking(false);
    setIsThinking(false);
    const el = remoteAudioElRef.current;
    const track = remoteAudioTrackRef.current;
    if (el && track) { try { track.detach(el); } catch (_) {} }
    remoteAudioTrackRef.current = null;
    try { await lkClientRef.current?.disconnect(); }
    catch (err) { console.warn('[TalkToVella] disconnect failed:', err); }
    setStatus("What do you need help with today?");
  }, []);

  // sendMessageRef is still used by the text path; keep the ref so existing
  // call sites don't break (the LiveKit voice path doesn't go through it).
  const sendMessageRef = useRef(null);

  const toggleVoiceMode = useCallback(() => {
    if (voiceActiveRef.current) stopVoice();
    else startVoice();
  }, [startVoice, stopVoice]);
  // Keep the ref pointing at the latest toggle so the global vella:toggleVoice
  // listener (installed once on mount) can still call the current closure.
  toggleVoiceModeRef.current = toggleVoiceMode;

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      voiceActiveRef.current = false;
      agentConnectedRef.current = false;
      if (agentJoinTimerRef.current) {
        clearTimeout(agentJoinTimerRef.current);
        agentJoinTimerRef.current = null;
      }
      const el = remoteAudioElRef.current;
      const track = remoteAudioTrackRef.current;
      if (el && track) { try { track.detach(el); } catch (_) {} }
      remoteAudioTrackRef.current = null;
      try { lkClientRef.current?.disconnect(); } catch (_) {}
      lkClientRef.current = null;
    };
  }, []);

  // Routed suggestions (Dashboard chips, "Redeploy", etc.) are seeded into the
  // docked InlineChatBar composer by App.jsx — NOT here. TalkToVella must never
  // auto-start voice: getUserMedia may only begin from the explicit mic
  // gesture (Operation Showroom global voice rule). vellaPrompt is intentionally
  // not consumed in this component.

  // Auto-scroll — only when the user is already near the bottom, so manual
  // scroll-up to read history isn't interrupted.
  useEffect(() => {
    if (isNearBottomRef.current && messagesEndRef.current) {
      try { messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" }); }
      catch (_) {}
    }
  }, [messages.length, isSending]);

  // Receive turns from the global InlineChatBar (rendered as a sibling dock
  // by App.jsx). Embedded mode in App.jsx dispatches one event per turn
  // (role: "user" before sending, role: "assistant" after the reply lands),
  // and isSending mirrors the in-flight state so the typing dots animate.
  useEffect(() => {
    const onChatbarMessage = (e) => {
      const role = e.detail?.role;
      const content = e.detail?.content;
      if (!role || typeof content !== "string") return;
      // The InlineChatBar now forwards the backend's structured side-channel
      // (widgets + structured_preview) alongside the text reply.
      const incomingWidgets = Array.isArray(e.detail?.widgets) ? e.detail.widgets : null;
      const structuredPreview = e.detail?.structured_preview || null;
      const routing = e.detail?.routing || null;
      const respondedModel = e.detail?.model || null;
      if (role === "assistant") {
        // The reply may also carry a generated website as a ```html``` block or
        // raw <!DOCTYPE> document (backend injects it via
        // injectGeneratedWebsiteHtml). Prefer the backend widgets when present
        // (covers creative / analytics too); synthesize a website_preview from
        // the inline HTML only when the backend didn't already ship one. Either
        // way, scrub the markup out of the bubble so source never leaks into a
        // chat bubble and the split preview panel lights up from one source.
        let displayText = content;
        let widgets = incomingWidgets;
        const synth = extractWebsiteHtml(content);
        if (synth) {
          const hasWebsite = (widgets || []).some(
            (w) => w?.type === "website" || w?.type === "website_preview"
          );
          widgets = hasWebsite ? widgets : [...(widgets || []), synth.widget];
          displayText = synth.cleanText || WEBSITE_BUILT_FALLBACK;
        } else if (HAS_MARKUP_RE.test(content)) {
          displayText = stripWebsiteMarkup(content) || content;
        }
        setMessages((prev) => [
          ...prev,
          { role, content: displayText, widgets, structured_preview: structuredPreview, routing, model: respondedModel, ts: Date.now() },
        ]);
        setIsSending(false);
        return;
      }
      setMessages((prev) => [...prev, { role, content, ts: Date.now() }]);
      setIsSending(true);
    };
    window.addEventListener("vella:chatbarMessage", onChatbarMessage);
    return () => window.removeEventListener("vella:chatbarMessage", onChatbarMessage);
  }, []);

  // Conversation switch / new — driven by the slide-over ConversationSidebar
  // (rendered at the App shell). Loads that conversation's messages into the
  // thread so the menu's "New task" and Recents actually navigate.
  useEffect(() => {
    const onSwitch = (e) => {
      const id = e.detail?.id;
      if (!id) return;
      const convo = getConversation(id);
      setConversationId(id);
      setMessages(convo?.messages || []);
      setTranscript("");
      setResponse("");
      setInputText("");
    };
    window.addEventListener("vella:conversationSwitch", onSwitch);
    return () => window.removeEventListener("vella:conversationSwitch", onSwitch);
  }, []);

  // ─── File upload handler ─────────────────────────────────────────────
  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';
    setUploadedFile(file);
    setUploadedFileUrl(null);
    setIsUploading(true);
    try {
      // Read as a base64 data URL — this flows end-to-end to the backend as a
      // Claude vision/document attachment AND doubles as the <img> preview src.
      // (The old /api/ai/upload endpoint never existed; data URLs avoid it.)
      const dataUrl = await fileToDataUrl(file);
      setUploadedFileUrl(dataUrl);
    } catch (err) {
      // Last-ditch local preview; won't reach the backend but won't crash.
      try { setUploadedFileUrl(URL.createObjectURL(file)); } catch (_) {}
    } finally {
      setIsUploading(false);
    }
  }, []);

  const clearUpload = useCallback(() => {
    setUploadedFile(null);
    setUploadedFileUrl(null);
  }, []);

  // ─── Action dispatch from widgets ────────────────────────────────────
  const handleWidgetAction = useCallback((action) => {
    if (!action) return;
    if (action.type === 'navigate') {
      setActive?.(action.view);
      return;
    }
    if (action.type === 'send_message') {
      if (action.focus) {
        setInputText(action.text || '');
      } else {
        sendMessage(action.text || '');
      }
      return;
    }
    if (action.type === 'action') {
      const cues = {
        analytics_detail:     "Show me a more detailed analytics breakdown.",
        analytics_compare:    "Compare this week's analytics to last week.",
        export:               "Export a report of this data.",
        post_now:             "Post this content now.",
        schedule:             "Schedule this post for the best time this week.",
        edit:                 "I'd like to edit this content.",
        regenerate:           "Try a different version of this.",
        // Creative chips
        another_angle:        "Show me another angle of that.",
        different_look:       "Try a different look or style for that.",
        use_this:             "Looks good — let's keep that one.",
        pause_campaign:       "Pause the current campaign.",
        increase_budget:      "Increase the campaign budget by 20%.",
        view_campaign_report: "Show me the full campaign performance report.",
        goto_inbox:           null, // navigate
        goto_website:         null,
        goto_web_studio:      null,
        preview_site:         null, // navigate
        edit_site:            null, // navigate
      };
      const navigateTo = {
        goto_inbox: 'inbox',
        goto_website: 'website',
        goto_web_studio: 'website',
        preview_site: 'website',
        edit_site: 'website',
      };
      if (navigateTo[action.action]) {
        setActive?.(navigateTo[action.action]);
      } else if (cues[action.action]) {
        sendMessage(cues[action.action]);
      }
    }
  }, [setActive]); // sendMessage is captured via ref below — stable

  // ─── Text send path ──────────────────────────────────────────────────
  // Returns the assistant text so the voice loop can pipe it into TTS.
  // Returns "" when the send was skipped (busy / empty input) or failed.
  const sendMessage = useCallback(async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || isSending) return "";
    sendFailedRef.current = null;
    setIsSending(true);

    // Real attachment goes through the `attachments` channel (→ backend Claude
    // vision/document block), NOT inlined into the prompt text. The note is a
    // short hint only — never the base64 URL (which would bloat the prompt).
    const hasAttachment = !!uploadedFileUrl && /^data:/.test(uploadedFileUrl);
    const attachments = hasAttachment ? [uploadedFileUrl] : null;
    const attachmentNote = uploadedFileUrl ? ' [Image attached]' : '';
    const fullText = text + attachmentNote;
    const userMsg = { role: "user", content: text, attachment: uploadedFileUrl || null, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    clearUpload();

    let assistantText = "";
    try {
      const priorTurns = messages.slice(-8).map(m => ({ role: m.role, content: m.content }));

      if (swarmMode) {
        // ── Swarm path ──────────────────────────────────────────────
        const resp = await aiSwarm("", fullText, priorTurns);
        assistantText = resp?.reply ?? "";
        const swarmWidgets = resp?.widgets ? [...resp.widgets] : null;
        const hasWebsite = swarmWidgets?.some((w) => w?.type === 'website' || w?.type === 'website_preview');
        let displayText = assistantText;
        let mergedWidgets = swarmWidgets;
        if (!hasWebsite) {
          const synth = extractWebsiteHtml(assistantText);
          if (synth) {
            mergedWidgets = [...(swarmWidgets || []), synth.widget];
            displayText = synth.cleanText || WEBSITE_BUILT_FALLBACK;
          }
        } else if (HAS_MARKUP_RE.test(assistantText)) {
          // Backend already returned a website widget — scrub any raw markup
          // that leaked into the prose so it never shows in the bubble.
          displayText = stripWebsiteMarkup(assistantText) || WEBSITE_BUILT_FALLBACK;
        }
        setMessages(prev => [...prev, {
          role: "assistant",
          content: displayText || "(empty response)",
          structured_preview: resp?.structured_preview || null,
          widgets: mergedWidgets,
          swarm_agents: resp?.agents?.length ? resp.agents : null,
          ts: Date.now(),
        }]);
      } else {
        // ── Standard path ────────────────────────────────────────────
        const resp = await aiCustom("", fullText, priorTurns, attachments ? { attachments } : {});
        assistantText = typeof resp === "string"
          ? resp
          : (resp?.reply ?? resp?.text ?? resp?.response ?? "");
        const structuredPreview = resp && typeof resp === "object" ? (resp.structured_preview || null) : null;
        const rawWidgets = resp && typeof resp === "object" ? (resp.widgets || null) : null;
        const hasWebsite = rawWidgets?.some((w) => w?.type === 'website' || w?.type === 'website_preview');
        let displayText = assistantText;
        let widgets = rawWidgets;
        if (!hasWebsite) {
          const synth = extractWebsiteHtml(assistantText);
          if (synth) {
            widgets = [...(rawWidgets || []), synth.widget];
            displayText = synth.cleanText || WEBSITE_BUILT_FALLBACK;
          }
        } else if (assistantText && HAS_MARKUP_RE.test(assistantText)) {
          // Backend already returned a website widget — scrub any raw markup
          // that leaked into the prose so it never shows in the bubble.
          displayText = stripWebsiteMarkup(assistantText) || WEBSITE_BUILT_FALLBACK;
        }
        setMessages(prev => [...prev, {
          role: "assistant",
          content: displayText || "(empty response)",
          structured_preview: structuredPreview,
          widgets,
          ts: Date.now(),
        }]);
      }
    } catch (err) {
      console.error("[TalkToVella] text send failed:", err);
      assistantText = "";
      // Signal failure so handleTextSend restores the composer, and stash the
      // original text on the error bubble so its Retry button can resend it.
      sendFailedRef.current = text;
      setMessages(prev => [...prev, {
        role: "assistant",
        content: describeBrainError(err),
        error: true,
        retryText: text,
        ts: Date.now(),
      }]);
    } finally {
      setIsSending(false);
    }
    return assistantText;
  }, [isSending, messages, swarmMode, uploadedFileUrl, clearUpload]);

  // Keep the voice loop's onTranscript handler pointing at the latest
  // sendMessage closure (it captures `messages` so it changes each render).
  useEffect(() => { sendMessageRef.current = sendMessage; }, [sendMessage]);

  const handleTextSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text) return;
    setInputText("");
    await sendMessage(text);
    // If the send failed, put the text back in the composer so the user can
    // edit/resend without retyping (the error bubble also offers a Retry).
    if (sendFailedRef.current === text) {
      setInputText(text);
      sendFailedRef.current = null;
    }
  }, [inputText, sendMessage]);

  const handlePreviewAction = useCallback((cue) => {
    if (!cue) return;
    sendMessage(cue);
  }, [sendMessage]);

  const hasConversation = !!(transcript || response || messages.length > 0);
  // Orb is a thinking indicator only — visible while Vella is processing
  // a chat turn (text or voice reply in flight), hidden otherwise.
  const orbVisible = isThinking || isSending;
  const lastUserContent = messages.filter(m => m.role === "user").slice(-1)[0]?.content || "";
  const lastAssistantMsg = messages.filter(m => m.role === "assistant").slice(-1)[0];

  // Drive the morphing VellaOrb off the chat lifecycle. Generation (text or
  // voice reply in flight) wins; otherwise we classify the finished turn into
  // a previewable result (website / image / ad / post) so the orb morphs to a
  // "result" card and the split panel can open. Plain replies → idle.
  const result = (!isSending && lastAssistantMsg) ? deriveResult(lastAssistantMsg) : null;
  const previewType = result?.previewType || null;
  const orbResult = result?.orb || null;
  // The unified morph orb (fixed, persistent) now owns BOTH the "generating"
  // card and the "thinking" spinner — see the LivingOrb render below. The old
  // in-flow VellaOrb "generating" box + ShiningText overlay are gone. What
  // remains here is the RESULT affordance card ("Here's what Vella made" +
  // iteration chips), shown once a finished turn produced a previewable result.
  const showResultCard = !isSending && !!orbResult;
  // Placement of the unified orb: center-stage on the empty home, docked above
  // the chat bar once a conversation exists. The glide fires once (per convo)
  // when hasConversation flips true on the first message.
  const orbPlacement = hasConversation ? 'docked' : 'centerstage';
  // Reserve space at the bottom of the message list so the docked card (250px
  // card + Stop button + gap) / docked orb never covers the last messages
  // (layout-shift guard). Card reserve matches the raised card dock so the last
  // message clears the whole card+Stop block. (D5)
  const dockReserve = orbMode === 'card' ? 340 : (livingOrbState !== 'idle' ? 150 : 0);

  // ─── Split preview panel ───────────────────────────────────────────────
  // When a finished turn produced a website or a creative, surface it in a
  // right-docked frosted-glass split panel (Claude Design "split preview").
  // Additive to the inline cards — closing it falls back to the in-thread
  // preview. Suppressed on mobile (no room for a split).
  const PANEL_W = '60vw';
  const previewPayload = result?.payload
    ? { ...result.payload, prompt: result.payload.prompt || lastUserContent || undefined }
    : null;
  const previewKey = previewPayload
    ? `${previewType}:${previewPayload.title}:${previewPayload.url || previewPayload.image || (previewPayload.html ? 'html' : '')}`
    : '';
  const [previewOpen, setPreviewOpen] = useState(false);
  const [orbEdit, setOrbEdit] = useState('');
  const [editToast, setEditToast] = useState('');
  const prevPreviewKeyRef = useRef('');
  // Track whether the latest result is a usable website so the Edit toolbar
  // chip (App shell) can re-open the editor or, if none yet, show a toast.
  const websiteReadyRef = useRef(false);
  websiteReadyRef.current = !!(previewPayload && previewPayload.kind === 'website' && (previewPayload.html || previewPayload.url));
  useEffect(() => {
    if (isMobile) { setPreviewOpen(false); return; }
    if (previewKey && previewKey !== prevPreviewKeyRef.current) {
      prevPreviewKeyRef.current = previewKey;
      setPreviewOpen(true);
    } else if (!previewKey) {
      prevPreviewKeyRef.current = '';
      setPreviewOpen(false);
    }
  }, [previewKey, isMobile]);
  const panelActuallyOpen = previewOpen && !!previewPayload && !isMobile;
  // Live mirror for window-event handlers (they can't see fresh state).
  const panelOpenRef = useRef(false);
  panelOpenRef.current = panelActuallyOpen;
  // Tell App.jsx to make room for the panel (pads the composer dock).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('vella:previewPanel', { detail: { open: panelActuallyOpen, width: PANEL_W } }));
    return () => window.dispatchEvent(new CustomEvent('vella:previewPanel', { detail: { open: false, width: PANEL_W } }));
  }, [panelActuallyOpen]);

  // Edit toolbar (App shell) → open the full website editor split view for the
  // latest generated site, or toast a hint when there's nothing to edit yet.
  useEffect(() => {
    const onEditToolbar = (e) => {
      if (e.detail?.action !== 'edit') return;
      if (isMobile) return;
      if (websiteReadyRef.current) {
        setPreviewOpen(true);
      } else {
        setEditToast('Generate a website first, then use Edit to customize it.');
      }
    };
    window.addEventListener('vella:editToolbar', onEditToolbar);
    return () => window.removeEventListener('vella:editToolbar', onEditToolbar);
  }, [isMobile]);

  // Wired toolbar chips (Mark up / Comments / Edit / + Tweaks / Zoom / Present)
  // → ResultPreviewPanel handles the action when it's mounted. When the panel
  // is closed but a website exists, open it and replay the action once the
  // panel has mounted so the first chip click still lands; when there's no
  // website yet, toast the same hint Edit shows.
  useEffect(() => {
    const PANEL_ACTIONS = ['markup', 'comments', 'edit', 'tweaks', 'zoom', 'present'];
    const onToolbar = (e) => {
      const action = e.detail?.action;
      if (!PANEL_ACTIONS.includes(action) || isMobile) return;
      if (!websiteReadyRef.current) {
        // The legacy vella:editToolbar listener already toasts for Edit.
        if (action !== 'edit') setEditToast('Generate a website first, then use the toolbar on its preview.');
        return;
      }
      if (!panelOpenRef.current) {
        setPreviewOpen(true);
        if (!e.detail?.replayed) {
          const detail = { ...e.detail, replayed: true };
          setTimeout(() => window.dispatchEvent(new CustomEvent('vella:toolbar', { detail })), 320);
        }
      }
    };
    window.addEventListener('vella:toolbar', onToolbar);
    return () => window.removeEventListener('vella:toolbar', onToolbar);
  }, [isMobile]);

  // Auto-dismiss the edit toast.
  useEffect(() => {
    if (!editToast) return;
    const t = setTimeout(() => setEditToast(''), 3200);
    return () => clearTimeout(t);
  }, [editToast]);

  const handleOrbResult = useCallback(() => {
    if (isMobile && orbResult?.url) { window.open(orbResult.url, '_blank', 'noopener'); return; }
    setPreviewOpen(true);
  }, [isMobile, orbResult]);
  // "New" under a result → start a fresh conversation.
  const handleNew = useCallback(() => {
    const id = newConversation();
    handleSidebarNew(id);
  }, [handleSidebarNew]);
  // Inline "Edit something specific…" composer under the orb result.
  const handleOrbEditSend = useCallback(() => {
    const t = orbEdit.trim();
    if (!t) return;
    setOrbEdit('');
    sendMessage(t);
  }, [orbEdit, sendMessage]);
  const isCreativeMode = hasConversation && CREATIVE_RE.test(lastUserContent) && !lastAssistantMsg?.structured_preview && !isSending;

  // INVARIANT: Each assistant message renders AT MOST ONE preview.
  return (
    <div style={{
      // Fill the (overflow-hidden) page-content box exactly — +48px compensates
      // the -24px vertical margins so the empty home never overflows into a
      // page-level scrollbar. Message scrolling lives in .vella-relay-scroll.
      height: "calc(100% + 48px)",
      margin: "-24px -32px",
      display: "flex",
      flexDirection: "column",
      fontFamily: FONT,
      // Scroll lock before the first message: the empty home is a fixed
      // composition (heading + orb + chips + bar); scrolling resumes once a
      // conversation exists.
      overflowY: hasConversation ? "auto" : "hidden",
      overflowX: "hidden",
      background: "transparent",
      alignItems: "center",
      justifyContent: hasConversation ? "flex-start" : "center",
      position: "relative",
      paddingRight: panelActuallyOpen ? PANEL_W : 0,
      transition: "padding-right 280ms cubic-bezier(0.22,1,0.36,1)",
    }}>
      {/* Breath Grid — dot-lattice + frosted veil fixed behind the chat column.
          First child, absolute inset:0 z-index:0; the header + message scroll
          below sit at z-index:1 so they layer above it (and scroll independently
          inside .vella-relay-scroll, so the grid never scrolls with messages). */}
      <BreathGrid />

      {/* Unified morph orb — ONE persistent element for every state and
          placement. It is BOTH the Living Orb AND the generating card: it
          glides center→dock on the first message and morphs orb⇄card for
          artifact jobs. Mounted once here so it never unmounts mid-morph. */}
      <LivingOrb
        state={livingOrbState}
        mode={orbMode}
        placement={orbPlacement}
        taskType={orbTaskType}
        dockOffset={orbMode === 'card' ? 200 : 132}
        onStop={() => {
          window.dispatchEvent(new Event('vella:orbStop'));
          window.dispatchEvent(new CustomEvent('vella:stopGeneration'));
        }}
        onClick={hasConversation ? undefined : () => {
          const ta = document.querySelector('textarea');
          if (ta) { ta.focus(); ta.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        }}
      />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes orbGlow { 0%,100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.05); } }
        @keyframes orbMorph0 { 0% { transform: scale(1) rotate(0deg); } 25% { transform: scale(1.03) rotate(0.8deg); } 50% { transform: scale(0.98) rotate(-0.4deg); } 75% { transform: scale(1.02) rotate(0.6deg); } 100% { transform: scale(1) rotate(0deg); } }
        @keyframes orbMorph1 { 0% { transform: scale(1) rotate(0deg); } 33% { transform: scale(0.97) rotate(-1deg); } 66% { transform: scale(1.04) rotate(0.7deg); } 100% { transform: scale(1) rotate(0deg); } }
        @keyframes orbMorph2 { 0% { transform: scale(1) rotate(0deg); } 50% { transform: scale(1.05) rotate(1.2deg); } 100% { transform: scale(1) rotate(0deg); } }
        @keyframes orbMorph3 { 0% { transform: scale(1) rotate(0deg); } 30% { transform: scale(1.03) rotate(-0.9deg); } 60% { transform: scale(0.96) rotate(0.5deg); } 100% { transform: scale(1) rotate(0deg); } }
        @keyframes orbMorph4 { 0% { transform: scale(1) rotate(0deg); } 40% { transform: scale(0.97) rotate(1.1deg); } 70% { transform: scale(1.04) rotate(-0.6deg); } 100% { transform: scale(1) rotate(0deg); } }
        @keyframes orbMorph5 { 0% { transform: scale(1) rotate(0deg); } 25% { transform: scale(1.05) rotate(-0.7deg); } 75% { transform: scale(0.96) rotate(1deg); } 100% { transform: scale(1) rotate(0deg); } }
        @keyframes waveBar { 0%,100% { height: 8px; opacity: 0.3; } 50% { height: 24px; opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dotPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }
        @keyframes vellaOrbAppear { from { opacity: 0; transform: scale(0.82); } to { opacity: 1; transform: scale(1); } }
        .vella-theme-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      {/* LiveKit remote audio sink. Hidden — track.attach() sets srcObject and
          we explicitly play() in the agentAudio handler so Safari/Chrome
          autoplay policies don't silence Vella. */}
      <audio ref={remoteAudioElRef} autoPlay playsInline style={{ display: "none" }} />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.pdf,.doc,.docx"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />


      {/* ── Header: title / autopilot badge / status ── */}
      <div style={{
        flex: hasConversation ? "0 0 auto" : 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: hasConversation ? "flex-start" : "center",
        paddingTop: hasConversation ? 16 : 0,
        gap: hasConversation ? 0 : 24,
        transition: "padding-top 0.3s ease",
        // Layer above the BreathGrid (z-index:0). D7 — on the empty home the
        // hero headline lives inside this container, so it must sit ABOVE the
        // center-stage orb (z-index:20) rather than behind its glow. Once a
        // conversation exists the orb docks and this returns to z-index:1.
        position: "relative",
        zIndex: hasConversation ? 10 : 26,
      }}>
        {/* Empty-home hero headline — fixed just above the center-stage orb
            (the orb itself is the persistent fixed element rendered below). */}
        {!hasConversation && (
          <div style={{
            // D7 — headline sits above the orb (z-index:25 > orb 20) with extra
            // clearance (178px) so the orb's glow never intersects/washes out
            // the text. position:relative isn't enough for a fixed element, so
            // the z-index rides on the raised parent container above.
            position: 'fixed', left: '50%', bottom: 'calc(50% + 178px)',
            transform: 'translateX(-50%)', zIndex: 25, width: '100%',
            maxWidth: 680, padding: '0 24px', textAlign: 'center',
            pointerEvents: 'none',
          }}>
            <style>{`
              .vella-headline { font-family: var(--font-heading); font-size: 34px; font-weight: 700; letter-spacing: -0.6px; margin: 0; text-align: center; line-height: 1.1; }
            `}</style>
            <h1 className="vella-headline">
              <ShiningText
                text="What can Vella do for you?"
                color="#4D71FF"
                style={{
                  fontSize: 'inherit', fontWeight: 'inherit', fontFamily: 'inherit',
                  letterSpacing: 'inherit', lineHeight: 'inherit',
                  // Item 6 — shimmer rebuilt from orb tokens: base #4D71FF,
                  // sweep #9EC5F5 → #fff, slowed 2s → 4.5s.
                  background: 'linear-gradient(110deg, #4D71FF 32%, #9EC5F5 46%, #fff 50%, #9EC5F5 54%, #4D71FF 68%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer-text 4.5s linear infinite',
                }}
              />
            </h1>
          </div>
        )}

        {/* Autopilot badge — only when active */}
        {autopilotOn && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 10 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 12px', borderRadius: 999,
              background: `${SUCCESS}14`, border: `1px solid ${SUCCESS}35`,
              animation: 'slideUp 0.3s ease',
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: SUCCESS, animation: 'orbGlow 2s ease-in-out infinite' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: SUCCESS, letterSpacing: '0.03em' }}>Autopilot Active</span>
            </div>
          </div>
        )}

        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 14, padding: "10px 18px", marginBottom: 12, maxWidth: 420, animation: "slideUp 0.3s ease" }}>
            <p style={{ color: "#DC2626", margin: 0, fontSize: 13 }}>{error}</p>
          </div>
        )}

        {(isListening || isSpeaking) && (
          <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 28, marginTop: 4 }}>
            {[0, 0.1, 0.2, 0.3, 0.4].map((d, i) => (
              <div key={i} style={{ width: 4, borderRadius: 2, background: isSpeaking ? "#10B981" : "#3B82F6", animation: `waveBar 0.8s ${d}s ease-in-out infinite` }} />
            ))}
          </div>
        )}
        {(voiceActive || isThinking || isSpeaking || isListening) && (
          <p style={{
            fontSize: 14, fontWeight: 500,
            color: isThinking ? "#0284C7" : isSpeaking ? "#10B981" : "#0EA5E9",
            margin: (isListening || isSpeaking) ? "10px 0 0" : "8px 0 0",
            animation: "slideUp 0.3s ease",
            letterSpacing: "-0.01em",
            minHeight: 20,
          }}>
            {voiceActive && !isListening && !isSpeaking && !isThinking ? "Tap mic to end conversation" : isThinking ? "Working on it..." : status}
          </p>
        )}

        {/* The unified morph orb (fixed layer, rendered near the top of this
            component) now owns BOTH the docked "thinking" spinner AND the
            "generating" card. What stays here is only the RESULT affordance —
            the "Here's what Vella made" card, iteration chips, and inline edit
            composer — shown once a finished turn produced a previewable result. */}
        {hasConversation && showResultCard && (
          <div
            data-vella-chrome="result-card"
            style={{
              marginTop: 12,
              position: 'relative',
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              width: "100%",
              maxWidth: 460,
              marginLeft: "auto",
              marginRight: "auto",
              pointerEvents: "auto",
            }}
          >
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: BLUE, letterSpacing: "-0.01em" }}>
              Here's what Vella made
            </p>
            <div style={{ pointerEvents: "auto" }}>
            <VellaOrb
              state="result"
              result={orbResult}
              onClick={handleOrbResult}
              size={150}
            />
            </div>

            {/* Result actions + iteration chips + inline edit composer. */}
            {(
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: "100%", animation: "slideUp 0.3s ease" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <OrbActionBtn primary onClick={handleOrbResult}>Open &amp; edit</OrbActionBtn>
                  <OrbActionBtn onClick={handleNew}>New</OrbActionBtn>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 }}>
                  <OrbChip onClick={() => handlePreviewAction("Show me another angle of that.")}>Another angle</OrbChip>
                  <OrbChip onClick={() => handlePreviewAction("Try a different look or style for that.")}>Different look</OrbChip>
                  <OrbChip primary onClick={() => handlePreviewAction("Looks good — let's keep that one.")}>Looks good</OrbChip>
                </div>

                <form
                  onSubmit={(e) => { e.preventDefault(); handleOrbEditSend(); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "6px 6px 6px 14px", borderRadius: 999,
                    background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.62) 100%)",
                    backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)",
                    border: "1px solid rgba(255,255,255,0.6)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 8px 22px -14px rgba(39,92,115,0.22)",
                  }}
                >
                  <input
                    value={orbEdit}
                    onChange={(e) => setOrbEdit(e.target.value)}
                    placeholder="Edit something specific…"
                    aria-label="Edit something specific"
                    style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontFamily: FONT, fontSize: 13.5, color: TEXT }}
                  />
                  <button
                    type="submit"
                    disabled={!orbEdit.trim()}
                    aria-label="Send edit"
                    style={{
                      flexShrink: 0, width: 30, height: 30, borderRadius: "50%", border: "none",
                      cursor: orbEdit.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center",
                      background: orbEdit.trim() ? BLUE : "rgba(39,92,115,0.25)", color: "#fff",
                      transition: "background 160ms ease",
                    }}
                    data-no-vella-ring="true"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </button>
                </form>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Center: messages ── */}
      <style>{`
        .vella-relay-scroll::-webkit-scrollbar { display: none; }
      `}</style>
      <div className="vella-relay-scroll" onScroll={handleMessagesScroll} style={{
        flex: hasConversation ? 1 : "0 0 auto",
        width: "100%",
        maxWidth: 720,
        padding: hasConversation ? "8px 24px 16px" : "0 32px 0",
        // Layout-shift guard: reserve room at the bottom so the fixed docked
        // orb / generating card never covers the last messages. Smoothly
        // expands (card) / collapses (idle) as the orb morphs.
        paddingBottom: hasConversation ? 16 + dockReserve : 0,
        transition: "padding-bottom 400ms cubic-bezier(0.22,1,0.36,1)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: hasConversation ? 14 : 20,
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        // Layer above the BreathGrid (z-index:0); messages scroll inside here,
        // so the fixed grid behind never scrolls with them. PATCHWORK D4 z-map:
        // page content z:10 (below orb z:20, above breath-grid z:0).
        position: "relative",
        zIndex: 10,
      }}>
        {/* D6: the duplicate "You said / Vella said" transcript cards were
            removed. Voice turns now render as ordinary messages in the thread
            below (tagged source:"voice" → mic glyph on the role label), so
            there is a single message layer, not two. */}

        {/* ── Text message list ── */}
        {messages.length > 0 && (
          <div aria-label="Text conversation with Vella" style={{ width: "100%", display: "flex", flexDirection: "column", gap: 10, animation: "slideUp 0.3s ease" }}>
            {messages.map((m, i) => {
              if (m.role === "assistant") {
                const isWebsiteContent =
                  m.vellaContent?.type === 'website' ||
                  /<!DOCTYPE|<html[\s>]/i.test(m.content || '');
                if (isWebsiteContent) {
                  let html = m.vellaContent?.html || '';
                  if (!html) {
                    const fence = (m.content || '').match(/```(?:html|HTML)\s*\n([\s\S]+?)\n```/);
                    if (fence) html = fence[1];
                    else {
                      const doc = (m.content || '').match(/<!DOCTYPE[\s\S]+?<\/html>/i) || (m.content || '').match(/<html[\s\S]+?<\/html>/i);
                      html = doc ? doc[0] : (m.content || '');
                    }
                  }
                  return (
                    <div key={i} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <WebsitePreviewCardUi
                        html={html}
                        title={m.vellaContent?.title || m.vellaContent?.businessName || 'Your site'}
                        url={m.vellaContent?.url}
                        domain={m.vellaContent?.domain}
                      />
                      <MessageWidgets
                        widgets={m.widgets}
                        swarmAgents={m.swarm_agents}
                        onAction={handleWidgetAction}
                      />
                    </div>
                  );
                }
              }
              if (m.role === "assistant" && m.structured_preview) {
                const isLatest = i === messages.length - 1;
                // Only ever show the generated marketing caption inside the
                // device frame — never the assistant's reasoning text. If the
                // caption is missing or just echoes the chat message, drop it.
                const rawCaption = m.structured_preview.caption;
                const safeCaption =
                  rawCaption &&
                  rawCaption.trim() &&
                  rawCaption.trim() !== (m.content || '').trim()
                    ? rawCaption
                    : undefined;
                return (
                  <div key={i} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <VellaChatMessage
                      asset={m.structured_preview.asset}
                      caption={safeCaption}
                      hashtags={m.structured_preview.hashtags}
                      platform={m.structured_preview.platform}
                      postType={m.structured_preview.post_type}
                      variant={m.structured_preview.variant}
                      brand={m.structured_preview.brand}
                      onAnotherAngle={isLatest ? () => handlePreviewAction("Show me another angle of that.") : undefined}
                      onDifferentLook={isLatest ? () => handlePreviewAction("Try a different look or style for that.") : undefined}
                      onLooksGood={isLatest ? () => handlePreviewAction("Looks good — let's keep that one.") : undefined}
                      onUpscale={isLatest ? () => handlePreviewAction("Upscale this video to 4K resolution.") : undefined}
                    />
                    {/* Widgets below structured preview */}
                    <MessageWidgets
                      widgets={m.widgets}
                      swarmAgents={m.swarm_agents}
                      onAction={handleWidgetAction}
                    />
                  </div>
                );
              }
              const isUser = m.role === "user";
              return (
                <div key={i} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', gap: 6 }}>
                  {/* Attachment thumbnail */}
                  {m.attachment && (
                    <div style={{ alignSelf: 'flex-end' }}>
                      <img src={m.attachment} alt="Attachment" style={{ height: 80, borderRadius: 8, border: '1px solid rgba(14,165,233,0.35)', objectFit: 'cover' }} />
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    gap: 10,
                    maxWidth: '88%',
                  }}>
                    {isUser ? <UserAvatar size={30} /> : <VellaAvatar size={30} />}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, maxWidth: isUser ? '60%' : undefined, marginLeft: isUser ? 'auto' : undefined }}>
                      {/* Item 7 — no "You" label on user turns. D6 — voice turns
                          carry a mic glyph so a spoken message reads as spoken
                          without a separate transcript card. */}
                      {!isUser ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontSize: 10.5,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: TEXT3,
                          textAlign: 'left',
                        }}>
                          Vella
                          {m.source === 'voice' && <MicGlyph />}
                        </span>
                      ) : m.source === 'voice' ? (
                        <span style={{ alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: TEXT3 }}>
                          <MicGlyph />
                          Voice
                        </span>
                      ) : null}
                      <div style={{
                        // Item 7 — deep-blue user bubble (#0B2AC4).
                        background: isUser
                          ? "#0B2AC4"
                          : "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.66) 100%)",
                        backdropFilter: isUser ? "none" : "blur(28px) saturate(180%)",
                        WebkitBackdropFilter: isUser ? "none" : "blur(28px) saturate(180%)",
                        border: isUser
                          ? "1px solid rgba(11,42,196,0.55)"
                          : "1px solid rgba(255,255,255,0.5)",
                        borderRadius: isUser ? "20px 6px 20px 20px" : "6px 20px 20px 20px",
                        padding: "11px 15px",
                        fontSize: 14,
                        color: isUser ? "#ffffff" : TEXT,
                        // User messages stay plain pre-wrap; assistant text is
                        // rendered as real markdown below (no pre-wrap there).
                        lineHeight: isUser ? 1.55 : undefined,
                        whiteSpace: isUser ? "pre-wrap" : undefined,
                        userSelect: "text",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 6px 18px -10px rgba(39,92,115,0.22)",
                        wordBreak: 'break-word',
                      }}>
                        {isUser ? m.content : <Markdown>{m.content}</Markdown>}
                      </div>
                    </div>
                  </div>
                  {/* Widgets + inferred action buttons for assistant messages */}
                  {!isUser && (
                    <>
                      {/* Retry affordance on a failed turn — resends the exact
                          message that errored, without the user retyping. */}
                      {m.error && m.retryText && (
                        <div style={{ alignSelf: 'flex-start', paddingLeft: 42 }}>
                          <button
                            type="button"
                            onClick={() => sendMessage(m.retryText)}
                            disabled={isSending}
                            data-no-vella-ring="true"
                            className="liquid-press"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 7,
                              cursor: isSending ? 'default' : 'pointer', opacity: isSending ? 0.55 : 1,
                              fontFamily: FONT, fontSize: 13, fontWeight: 600,
                              padding: '7px 16px', borderRadius: 999, color: BLUE,
                              background: 'rgba(255,255,255,0.72)',
                              border: `1px solid ${BORDER}`,
                              backdropFilter: 'blur(18px) saturate(160%)', WebkitBackdropFilter: 'blur(18px) saturate(160%)',
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85), 0 8px 22px -14px rgba(39,92,115,0.3)',
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
                            Retry
                          </button>
                        </div>
                      )}
                      <MessageWidgets
                        widgets={m.widgets}
                        swarmAgents={m.swarm_agents}
                        onAction={handleWidgetAction}
                      />
                      {/* Client-side fallback action buttons (only on last message, when no explicit widgets) */}
                      {i === messages.length - 1 && !m.error && !m.widgets?.length && !m.swarm_agents?.length && !isSending && (() => {
                        const actions = inferContextActions(m.content);
                        return actions ? (
                          <div style={{ alignSelf: 'flex-start', paddingLeft: 42 }}>
                            <ActionButtons buttons={actions} onAction={handleWidgetAction} />
                          </div>
                        ) : null;
                      })()}
                    </>
                  )}
                </div>
              );
            })}
            {/* D5 — ONE status indicator. While the orb has morphed into the
                generating card (orbMode === 'card') the card already shows the
                live "Designing…/Rendering…" status, so suppress this separate
                "Thinking…" bubble to avoid a double status. */}
            {isSending && orbMode !== 'card' && (
              <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 10, paddingTop: 2 }}>
                <VellaAvatar size={30} />
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.66) 100%)',
                  backdropFilter: 'blur(28px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
                  border: '1px solid rgba(255,255,255,0.5)',
                  borderRadius: '6px 20px 20px 20px',
                  padding: '11px 15px',
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  fontSize: 13,
                  color: BLUE,
                  fontWeight: 600,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px -10px rgba(39,92,115,0.18)',
                }}>
                  <span>{swarmMode ? 'Swarm thinking' : 'Thinking'}</span>
                  {[0, 0.15, 0.3].map((d, i) => (
                    <span key={i} style={{ animation: `dotPulse 1.2s ${d}s ease-in-out infinite` }}>.</span>
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Creative theme cards */}
        {isCreativeMode && (
          <div style={{ width: "100%", animation: "slideUp 0.35s ease" }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: TEXT3, textAlign: "center", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>Choose a creative style</p>
            <div className="vella-theme-scroll" style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8, scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {CREATIVE_THEME_CARDS.map(card => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => sendMessage(`Use the "${card.label}" style for this creative.`)}
                  style={{ flexShrink: 0, width: 132, padding: "12px 14px", borderRadius: "var(--radius-md)", border: "1px solid rgba(255,255,255,0.5)", background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.66) 100%)", backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)", textAlign: "left", cursor: "pointer", fontFamily: FONT, transition: "border-color 0.2s, transform 0.2s", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px -10px rgba(39,92,115,0.18)" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--blue-300)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)"; e.currentTarget.style.transform = "none"; }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: TEXT, marginBottom: 4, lineHeight: 1.3 }}>{card.label}</div>
                  <div style={{ fontSize: 11, color: TEXT3, lineHeight: 1.4 }}>{card.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Vella Relay uses the global InlineChatBar (in App.jsx) for text
          input. No secondary chatbar lives in this component. */}

      {/* Edit-toolbar toast — shown when "Edit" is clicked with no site yet */}
      {editToast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', top: 116, left: '50%', transform: 'translateX(-50%)',
            zIndex: 60, background: '#0f2932', color: '#fff', borderRadius: 14,
            padding: '10px 16px', fontSize: 13, fontWeight: 500, fontFamily: FONT,
            boxShadow: '0 12px 32px -8px rgba(15,41,50,0.45)', maxWidth: 'min(90vw, 420px)',
            animation: 'slideUp 0.24s ease',
          }}
        >
          {editToast}
        </div>
      )}

      {/* Split preview panel — right-docked result viewer
          (website / image / ad / post) */}
      {panelActuallyOpen && (
        <ResultPreviewPanel
          payload={previewPayload}
          width={PANEL_W}
          onClose={() => setPreviewOpen(false)}
          onRegenerate={() => sendMessage(previewPayload?.kind === 'website' ? 'Regenerate the website with a fresh design.' : 'Try a different look or style for that.')}
          onSelectVariation={(idx) => sendMessage(`Use variation ${idx + 1} as the main creative.`)}
          onRewriteCopy={() => sendMessage('Rewrite the copy for this creative.')}
          onAction={(a) => {
            if (a?.action === 'ai_tweak') { sendMessage(a.value); return; }
            if (a?.action === 'edit_site') { setActive?.('website'); return; }
            if (a?.action === 'publish_site') { sendMessage('Publish my website and give me the live link.'); return; }
            if (a?.action === 'launch_creative') { sendMessage('Launch this creative as an ad.'); return; }
            if (a?.action === 'set_format') { sendMessage(`Resize this creative to a ${a.value} aspect ratio.`); return; }
            if (a?.action === 'set_button_color') { sendMessage(`Change the call-to-action button color to ${a.value}.`); return; }
            if (a?.action === 'set_accent') { sendMessage(`Use ${a.value} as the website's accent color throughout.`); return; }
            if (a?.action === 'set_typeface') { sendMessage(`Switch the website's typeface to ${a.value}.`); return; }
            if (a?.action === 'upload_logo') { sendMessage('I want to use my own logo on the website — swap the placeholder logo for mine.'); return; }
            if (a?.action === 'buy_domain') { sendMessage(`Help me buy the domain ${a.value} for this website.`); return; }
            handleWidgetAction(a);
          }}
          onChip={(kind) => {
            if (kind === 'good') { setPreviewOpen(false); return; }
            if (previewPayload?.kind === 'website') {
              sendMessage(kind === 'another' ? 'Show me a different layout for this website.' : 'Give the website a different visual style.');
              return;
            }
            sendMessage(kind === 'another' ? 'Show me another angle of that.' : 'Try a different look or style for that.');
          }}
        />
      )}

    </div>
  );
}